/**
 * Analytics-wide period selection. A single value drives every tab on
 * `/analytics` (Overview, Details, Estimation, Quality).
 *
 * All modes resolve to an effective `(startDate, endDate)` on the backend so
 * every aggregation can reuse the same SQL shape.
 */
export type AnalyticsPeriod =
  | {
      mode: 'dateRange';
      startDate: string;
      endDate: string;
      /** Quick-filter preset id (e.g. today, thisWeek, lastMonth) when user picked one. */
      presetKey?: string;
    }
  | {
      mode: 'sprint';
      sprintId: string;
      sprintName: string;
    }
  | {
      mode: 'release';
      fixVersion: string;
    }
  | {
      mode: 'quarter';
      /** `YYYY-Q#`, e.g. `2026-Q1`. */
      quarter: string;
    };

export interface SprintOption {
  id: string;
  name: string;
  startDate?: string;
  endDate?: string;
  state?: 'active' | 'closed' | 'future';
  issueCount?: number;
}

export interface ReleaseOption {
  fixVersion: string;
  /** Latest resolution in this fix version (may be absent if nothing resolved yet). */
  releaseDate?: string;
  /** Earliest issue created in this fix version — used with maxUpdated for period bounds. */
  minCreated?: string;
  /** Latest issue update in this fix version. */
  maxUpdated?: string;
  /** Planned window from /releases calendar (e.g. Start Development → Date Release), ISO dates. */
  planStartDate?: string;
  planEndDate?: string;
  storyCount?: number;
  issueCount?: number;
}

export interface QuarterOption {
  id: string;
  label: string;
  startDate: string;
  endDate: string;
  issueCount?: number;
}

export interface PeriodOptions {
  sprints: SprintOption[];
  releases: ReleaseOption[];
  quarters: QuarterOption[];
}

/** Sprint list in PeriodPicker comes from the default board saved on Releases → Sprint Plan. */
export interface SprintPickerMeta {
  hasSavedDefaultBoard: boolean;
  listError: string | null;
}

export interface ResolvedPeriod {
  startDate: string;
  endDate: string;
  label: string;
}

export function resolvePeriodRange(
  period: AnalyticsPeriod,
  opts: PeriodOptions
): ResolvedPeriod | null {
  if (period.mode === 'dateRange') {
    if (!period.startDate || !period.endDate) return null;
    const label = `${period.startDate} – ${period.endDate}`;
    return { startDate: period.startDate, endDate: period.endDate, label };
  }
  if (period.mode === 'sprint') {
    const s = opts.sprints.find((x) => x.id === period.sprintId);
    if (s?.startDate && s?.endDate) {
      return { startDate: s.startDate, endDate: s.endDate, label: s.name };
    }
    return null;
  }
  if (period.mode === 'release') {
    const r = opts.releases.find((x) => x.fixVersion === period.fixVersion);
    if (!r) return null;
    if (r.planStartDate && r.planEndDate) {
      let start = r.planStartDate;
      let end = r.planEndDate;
      if (start > end) {
        const t = start;
        start = end;
        end = t;
      }
      return { startDate: start, endDate: end, label: r.fixVersion };
    }
    const minC = r.minCreated;
    const rel = r.releaseDate;
    const maxU = r.maxUpdated;
    let end = rel || maxU || minC;
    let start = minC || rel || maxU;
    if (!start || !end) return null;
    if (start > end) {
      const t = start;
      start = end;
      end = t;
    }
    // Sanity check: if the fallback range spans > 6 months it is likely
    // wrong (e.g. minCreated from an old issue) — return null so the UI
    // can surface a clear warning instead of misleading data.
    const SIX_MONTHS_MS = 6 * 31 * 24 * 60 * 60 * 1000;
    const spanMs = new Date(end).getTime() - new Date(start).getTime();
    if (spanMs > SIX_MONTHS_MS) return null;
    return { startDate: start, endDate: end, label: r.fixVersion };
  }
  if (period.mode === 'quarter') {
    const q = opts.quarters.find((x) => x.id === period.quarter);
    if (q) return { startDate: q.startDate, endDate: q.endDate, label: q.label };
    return null;
  }
  return null;
}
